import { type Rubric } from '../../core/rubric';
import { evidenceQuotes, passExplanation, passMass, type ScoreResponse } from '../../core/scoring';
import { GRADER } from '../grader';

export interface ResultsProps {
  result: ScoreResponse;
  rubric: Rubric;
  selectedItemId: string | null;
  evidenceCutoff?: number;
  onEvidenceCutoffChange?: (percent: number) => void;
  onSelect: (itemId: string | null) => void;
  onEdit: () => void;
}

const percent = (n: number): string => `${Math.round(n * 100)}%`;

export default function Results({ result, rubric, selectedItemId, onSelect, onEdit, evidenceCutoff = 50, onEvidenceCutoffChange }: ResultsProps) {
  const scorer = result.provider === 'jev' ? `The ${GRADER}` : 'The sample scorer';
  const passName = rubric.items[0]?.levels[rubric.passLevel]?.name ?? 'the passing level';

  return (
    <section className="pane pane-rubric" aria-labelledby="results-heading">
      <div className="pane-head">
        <h2 id="results-heading" className="ph-mask">{rubric.title || 'Rubric'}</h2>
        <button type="button" className="secondary small" onClick={onEdit}>Edit rubric</button>
      </div>
      {/* Everything below is a model's estimate; say so before any number, in words a teacher would use. */}
      <p className="estimate-note">These are estimates, not a hard rating or grade. Use them as a starting point and check the highlighted sentences.</p>
      <p className="summary" role="status">
        <strong>Estimate: {result.passedCount} of {result.itemCount} likely meet the passing level</strong> ({passName} or better)
        {result.provider === 'fallback' && <span className="fallback-notice"> · Sample scoring — AI grader not connected</span>}
      </p>
      <p className="hint">Select a criterion to see the sentences behind its estimate.</p>
      <details className="evidence-filter">
        <summary>Evidence cutoff: <strong>{evidenceCutoff}%</strong></summary>
        <input id="evidence-cutoff" type="range" min="50" max="100" step="1" value={evidenceCutoff} aria-label="Evidence cutoff"
          onChange={(event) => onEvidenceCutoffChange?.(Number(event.target.value))} aria-describedby="evidence-cutoff-help" />
        <p id="evidence-cutoff-help" className="hint">Highlight only sentences at or above this evidence probability. Ratings do not change.</p>
      </details>
      <ol className="verdicts ph-no-capture">
        {result.items.map((item, index) => {
          const selected = item.id === selectedItemId;
          const rubricItem = rubric.items[index];
          const levelCount = rubricItem?.levels.length ?? item.levelProbabilities.length;
          const visibleEvidence = evidenceQuotes(item, result.sentences, evidenceCutoff / 100);
          const status = item.degraded ? 'Unavailable' : !item.applies ? 'Not addressed' : item.passes ? 'Met' : 'Not met';
          const label = { Met: 'Likely met', 'Not met': 'Likely not met', 'Not addressed': 'Not addressed', Unavailable: 'Unavailable' }[status];
          const rating = item.degraded ? 'No estimate' : !item.applies ? 'No level' : `Likely ${item.levelName} · ${item.level + 1} of ${levelCount}`;
          return (
            <li key={item.id} className={`verdict${item.passes ? ' pass' : ''}${item.applies ? '' : ' dimmed'}${item.degraded ? ' degraded' : ''}`}>
              <button type="button" className="verdict-button" aria-pressed={selected} aria-expanded={selected}
                aria-controls={`rating-detail-${item.id}`} aria-label={`${item.name}: ${rating}, ${label}. View evidence.`}
                onClick={() => onSelect(selected ? null : item.id)}>
                <span className="verdict-row"><span className="verdict-name">{item.name}</span><span className="status-badge" data-status={status}>{label}</span></span>
                <span className="verdict-level">{rating}</span>
              </button>
              {selected && <div className="rating-detail" id={`rating-detail-${item.id}`}>
                {item.degraded ? <p>{scorer} returned an incomplete answer for this criterion, so there is no rating.</p> : <>
                  <p>
                    {item.applies ? `${visibleEvidence.length} of ${result.sentences.length} sentences count as evidence at ${evidenceCutoff}% or above.` : `${scorer} found that the writing does not address this criterion.`}
                    {item.applies && visibleEvidence.length === 0 && ' Lower the cutoff to see weaker evidence.'}
                  </p>
                  {rubricItem?.description && <p className="criterion-description ph-mask"><strong>What it measures:</strong> {rubricItem.description}</p>}
                  <ol className="level-breakdown" aria-label="Levels for this criterion, lowest to highest">
                    {rubricItem?.levels.map((level, levelIndex) => {
                      const chosen = item.applies && levelIndex === item.level;
                      return (
                        <li key={levelIndex} className={chosen ? 'chosen' : undefined} aria-current={chosen ? 'true' : undefined}>
                          <div className="level-head">
                            <span className="level-title">{level.name}{chosen && <span className="level-tag">Likely level</span>}{levelIndex === rubric.passLevel && <span className="level-tag pass-mark">Passing</span>}</span>
                            <progress max={1} value={item.levelProbabilities[levelIndex] ?? 0} aria-label={`${level.name} probability`} />
                            <span className="level-percent">{percent(item.levelProbabilities[levelIndex] ?? 0)}</span>
                          </div>
                          {level.text && <p className="level-text-read ph-mask">{level.text}</p>}
                        </li>
                      );
                    })}
                  </ol>
                  <p className="hint">{passExplanation({ applies: item.applies, passes: item.passes, mass: passMass(item.levelProbabilities, rubric.passLevel), passName })}</p>
                </>}
              </div>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
